import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  animate,
  motion,
  useAnimate,
  useMotionValue,
  type AnimationPlaybackControls,
} from "motion/react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";
import { useAppStore } from "@/stores";
import { selectMissionsCampaignById } from "@/stores/missions.store";
import { Badge } from "@/components/ui/badge";
import { CaptionCell } from "@/igw-button/caption-cell";
import { cn } from "@/lib/utils";
import { PrizeText } from "@/components/prize-text";
import { formatButtonPrizeTitle, formatPrizeTitle } from "@/utils/helpers";
import { TaskKindIcon } from "@/components/icons/task-kind-icons";
import { StepDoneIcon } from "@/components/icons/step-state-icons";
import { Gleam } from "@/igw-button/gleam";
import { METAL_FROM_X, METAL_SPRING, METAL_STAGGER } from "@/igw-button/metal";
import type { LauncherContentProps } from "../../registry";
import { missionView, type MissionView } from "./launcher-view";
import { currentMission } from "./types";
import { campaignKey as campaignKeyOf } from "@/campaigns/campaign-key";

/**
 * Missions on the in-game launcher — its own content, not the tournament's
 * layout (Sebastian, 2026-10-15: "this is a fresh start … mission content is
 * its own component"), in the same materials and type (the tournament is the
 * finished reference: "translate that a bit, use the same type of style").
 *
 * Out of the coin, top down in the stack:
 * - the task, a raised pill under the bar ("Play 200 rounds", the backend's
 *   own line), centred on the bar, stopped at the coin's edge, its type
 *   shrinking to 10px before it ends in an ellipsis;
 * - the progress bar on the coin's axis: the task's progress filling it in
 *   the progress token, brightest at its leading edge; the value riding
 *   that front (etched into it, or ahead of it on the track), each count
 *   landing as a beat that pushes the fill on, with the currency sign on
 *   money tasks; before any progress,
 *   the task kind's glyph etched into the track (the fill passes over it);
 * - the mission's prize in gold on a square banner, its foot tucked under
 *   the bar's top, faded at both ends (the prize is the mission's, never a
 *   task's, D-070).
 * The entry: metal (`igw-button/metal`) — the cell, its checklist and the
 * prize chip slide out from under the coin along x, a beat apart, and stop
 * dead; the task line slides into its band.
 */

function useMissionView(key: string) {
  return useAppStore(
    useShallow((s) => {
      const mc = selectMissionsCampaignById(s, key);
      return mc
        ? missionView(
            mc,
            s.currency,
            s.missionFocusById[campaignKeyOf(mc.campaign)]
          )
        : null;
    })
  );
}

/** A task done (Sebastian, 2026-10-16), on the same bar — never a fill
 *  running back (👺 Belmont, B3; IGB-MOTION-01): its last count lands and
 *  the fill springs full; then the win (`TASK_WON_AT_MS`) — the bar
 *  brightens, its gradient morphing, a shine runs off past its end, a big
 *  check stamps in and the gleam sweeps it; then the bar and the task are
 *  smacked into the coin, the coin takes the hit, and throws the next task
 *  out with the content's own entry — its bar growing in length only ("I
 *  only want to scale the X and not the Y axis on the bar here").
 *  Graded by what was won (Sebastian, 2026-10-16: "grade it", 👺 Belmont,
 *  T1 — only the step's payout lights the prize, D-070): the step's last
 *  task pays it out — the bar turns gold, the gleam sweeps twice, and the
 *  prize goes into the coin with them; a task inside a step keeps the
 *  progress token, one gleam, and the prize stays. */
const TASK_WON_AT_MS = 450;
/** Each part winds up first — drawn back out a touch, a small anticipation
 *  (Sebastian: "a small anticipation before smacking in") — then strikes;
 *  the parts one after another (Sebastian: "stagger the elements when going
 *  in the coin a bit"): the bar, the prize, the task. */
const SMACK_WINDUP_MS = 120;
/** The won beat's light, spread out one after another (Sebastian: "span
 *  out the staggering of the extras a bit … I meant the shines and
 *  gleams"): the shine's wave from the turn to gold (off the bar by
 *  ~0.75s), then the two gleams (`.igw-gleam--won-1/2`, their delays from
 *  the turn); the second's end — its delay + duration, 1.15s + 1.1s — is
 *  when the bar goes in ("so we go in just when the second gleam is done"),
 *  its wind-up started before. */
const GLEAM_1_ENDS_MS = 450 + 550;
const GLEAM_2_ENDS_MS = 1150 + 1100;
const SMACK_WINDUP = { type: "spring", stiffness: 700, damping: 26 } as const;
const SMACK_STAGGER_MS = 50;
/** The strike into the coin: stiff, no overshoot; landed after ~110ms. */
const SMACK_STRIKE = { type: "spring", stiffness: 1400, damping: 60 } as const;
const SMACK_LANDS_MS = 110;
/** A beat's timeline (ms from the done count): the smack once its last
 *  gleam is done; the coin's hit (the shell's, `CoinBeat`) as the bar lands,
 *  held while the rest land after it; then the recoil, on which the next
 *  task is thrown out. */
function beatTimes(paid: boolean, kind: Beat["kind"] = "done") {
  // A swap has no win to show: it goes straight into the coin.
  const smack =
    kind === "swap"
      ? 0
      : TASK_WON_AT_MS +
        (paid ? GLEAM_2_ENDS_MS : GLEAM_1_ENDS_MS) -
        SMACK_WINDUP_MS;
  const hit = smack + SMACK_WINDUP_MS + SMACK_LANDS_MS;
  // The bar, then the task — and the prize between them when it goes in.
  const recoil = hit + (paid ? 2 : 1) * SMACK_STAGGER_MS;
  return { smack, hit, recoil, throwAt: recoil };
}
/** Under reduced motion: the gold and the check held still, then the next
 *  task at once. */
const TASK_DONE_STILL_MS = 900;
/** The turn (bright; gold on a payout): firm, no overshoot (~0.35s). */
const WON_SPRING = { type: "spring", stiffness: 260, damping: 32 } as const;
/** The check locked in after the gold's shift, as a stamp (Sebastian,
 *  2026-10-16: "lock the check in after the gradient shift, so you get an
 *  impact stamp kind of, with a small rotated momentum"): once the turn to
 *  gold has settled (`CHECK_AT`, s after it), it slams down from twice its
 *  size, turned — the scale stiff and landing at once (the impact), the
 *  turn on a looser spring that carries past straight and settles back
 *  (the momentum). */
const CHECK_AT = 0.35;
const CHECK_FROM = { scale: 2.1, rotate: -16, opacity: 0 } as const;
const CHECK_STAMP = {
  scale: { type: "spring", stiffness: 1300, damping: 40, delay: CHECK_AT },
  rotate: { type: "spring", stiffness: 420, damping: 13, delay: CHECK_AT },
  opacity: { type: "spring", stiffness: 1600, damping: 60, delay: CHECK_AT },
} as const;
/** The task's pip in the map slammed into its ✓ at the same impact
 *  (Sebastian, 2026-10-19: "slam the task pip into a checkmark"). */
const PIP_FROM = { scale: 2.2, rotate: -24, opacity: 0 } as const;
const PIP_STAMP = CHECK_STAMP;
/** In order, the path moves on after the slam: the next task's lock breaks
 *  and its dot lights, then the window slides (Sebastian, 2026-10-19: "for
 *  tasks in order it must be much clearer that we are moving forward").
 *  After the check's impact, in ms. */
const ADVANCE_AT_MS = CHECK_AT * 1000 + 260;
/** The task line's swap: the old line drawn off toward the coin with the
 *  bar's smack, the new one in with the throw — the cell itself stays
 *  (Sebastian, 2026-10-19: "don't remove the task label cell, just replace
 *  the text itself, not the cell"). */
const LINE_OUT = { x: -10, opacity: 0 } as const;
const LINE_IN_FROM = { x: 10, opacity: 0 } as const;
/** Progress on the task on show kicks its pip (the same hit as the count's). */
const PIP_KICK_UP = { type: "spring", stiffness: 1200, damping: 30 } as const;
const PIP_KICK = { type: "spring", stiffness: 500, damping: 12 } as const;
/** The shine's wave travelling the won bar, from the coin and off past its
 *  end (`--igw-flow`, 0 → 1; Sebastian: "I want the shine to move across
 *  and morph … go past it and disappear"): critically damped, launched with
 *  some speed so it doesn't creep out from behind the coin; off the bar
 *  well before the smack (~1.2s; its slow tail is past the end). */
const FLOW_SPRING = {
  type: "spring",
  stiffness: 18,
  damping: 8.5,
  velocity: 0.8,
} as const;

/** A progress update: firm, with a small wobble (Sebastian, 2026-10-15: "it
 *  will behave a bit wobbly but firm with the springs update animation") —
 *  one short overshoot (~6% of the step; it was 17%, which on the smaller
 *  cell carried the fill past the end — Sebastian, 2026-10-20: "dial back
 *  the progress animation now when it's smaller"), settled in about half a
 *  second. */
const PROGRESS_SPRING = {
  type: "spring",
  stiffness: 380,
  damping: 26,
  mass: 1,
} as const;

/** The entry's beats: the cell a beat after the coin lands (as the
 *  shared set's lines, 0.12s), the checklist and the prize chip staggered
 *  after it (`METAL_STAGGER`; Sebastian, 2026-10-21: "use this type of
 *  entry on the other campaign types, stagger its different elements"), the
 *  task line with its band. */
const ENTRY_AT = 0.12;
const ENTRY_TASK_AT = ENTRY_AT + 0.4;
/** The bar's length: firm, no overshoot. */
const ENTRY_GROW = { type: "spring", stiffness: 380, damping: 30 } as const;
/** …then springs back up into it, tight (Sebastian: "revert the snap, work
 *  on the tightness of the spring instead"): stiff and quick, one small
 *  overshoot (~1.5px), settled in ~250ms. */
const ENTRY_SETTLE = { type: "spring", stiffness: 1200, damping: 30 } as const;

/** The smallest type on the content (the task line, the prize as it
 *  shrinks): the button's floor (IGB-CORE-05; Sebastian, 2026-10-15: "we
 *  can have 10 as floor", then "10px is the new floor" for the whole
 *  button). */
const FLOOR_PX = 10;
/** The task line's own floor, a step under the button's (Sebastian,
 *  2026-10-21: "set the text size floor to 9px" — the label shrinks to it
 *  before the cell widens). */
const TASK_FLOOR_PX = 9;
/** The prize band's type (`.igw-mission__prize`): the task's 11px — it
 *  leads by its gold, not by size (Sebastian, 2026-10-20: "the prize really
 *  sticks out, make the prize text smaller").
 *  On the fit ladder it steps once, to the button's 10px floor; past that
 *  the prize glides. */
const PRIZE_PX = 11;
const PRIZE_MIN_PX = FLOOR_PX;
/** The task map shows up to this many pips at once — as many as its row
 *  fits, never fewer than the floor; the edge pips with more beyond them are
 *  dimmed (Sebastian, 2026-10-19: "show a maximum of 6 tasks, the last task
 *  faded a bit so the player understands there are more"). */
const MAP_WINDOW_MAX = 6;
/** In order the path's links take room, and five reads as a path, not a
 *  row (Sebastian, 2026-10-19: "for in order we show a maximum of 5 tasks in
 *  the map"). */
const MAP_WINDOW_MAX_PATH = 5;
/** Never fewer, while the step has that many (Sebastian, 2026-10-20: "go
 *  with D and the 4-pip floor"). */
const MAP_WINDOW_MIN = 4;
/** The beads' end pad on the cell, px. */
const PIPS_END_PX = 4;

/** The value's first appearance: once the fill has begun to grow (s), then
 *  a soft rise in — one small overshoot. */
const VALUE_IN_DELAY = 0.3;
const VALUE_IN = { type: "spring", stiffness: 520, damping: 22 } as const;
/** A new count lands as a beat (Sebastian, 2026-10-15: "on update, the
 *  text springs a bit to the left, then impacts to the right, and the bar
 *  gets pushed into progress by it; the moment the impact occurs we swap
 *  the value"): the value winds up a few px left, then, at the hit, snaps
 *  right past its place and back — the number swapping and popping there,
 *  the fill springing on from that moment, pushed by it. */
const VALUE_WINDUP_PX = 5;
const VALUE_WINDUP = { type: "spring", stiffness: 700, damping: 24 } as const;
/** When the wound-up value hits (ms after the count arrives). */
const VALUE_HIT_AT_MS = 150;
const VALUE_HIT = {
  type: "spring",
  stiffness: 1400,
  damping: 26,
  velocity: 400,
} as const;
const VALUE_POP_IN = { type: "spring", stiffness: 1600, damping: 40 } as const;
const VALUE_POP_OUT = { type: "spring", stiffness: 900, damping: 30 } as const;
const VALUE_POP_HOLD_MS = 90;
/** The value changing sides of the front (inside it / ahead of it): the
 *  band around the threshold it must cross (px), and how long it slides to
 *  its new place (the CSS ease on `[data-sliding]`, well under 200ms). */
const VALUE_SIDE_BAND = 6;
const VALUE_SIDE_SLIDE_MS = 150;

/** A task done, held through its beat: its view at the end, and whether it
 *  paid its step out. */
/** `kind`: the task finished (its beat), or the task on show changed without
 *  finishing (any order: another task moved — a quiet swap, no win). */
type Beat = { view: MissionView; paid: boolean; kind: "done" | "swap" };

/** Any order: how long a task stays on show before the next takes its turn. */
const ROTATE_MS = 30_000;
/** Under reduced motion, how long a moved bead's rim holds the token. */
const MOVED_TINT_MS = 1000;

/** Any order rotates (Sebastian, 2026-10-22: "auto rotate on tasks that are
 *  not in order — start from the first and then we rotate between them on a
 *  30s interval"), and a turn is a turn ("a turn is a turn", over progress
 *  pulling the bar): each task on show holds its `ROTATE_MS` whatever moves
 *  meanwhile — a move off show kicks its bead — then the next unfinished one
 *  in order goes on show, wrapping ("strict order": handing the turn to the
 *  last moved let the busiest tasks take every turn in play, 👺 Belmont N1)
 *  — through the store's focus memory, so the control's name follows. */
/** The current step's counts, one per task (`id=count|…`): a move on a task
 *  off show is read from it. */
function useStepCounts(key: string): string {
  return useAppStore((s) => {
    const mc = selectMissionsCampaignById(s, key);
    const step = mc && currentMission(mc);
    return step
      ? step.tasks.map((t) => `${t.id}=${t.progress?.current ?? ""}`).join("|")
      : "";
  });
}

function useTaskRotation(view: MissionView | null, key: string) {
  const setFocus = useAppStore((s) => s.setMissionFocus);
  const taskKey = view?.taskKey ?? null;
  const ids = view?.taskIds ?? "";
  const pips = view?.pips ?? "";
  const inOrder = view?.inOrder ?? true;
  useEffect(() => {
    if (inOrder || !taskKey) return;
    const all = ids.split(",");
    const open = all.filter((_, i) => pips[i] === "c" || pips[i] === "o");
    if (open.length < 2) return;
    const current = taskKey.split(":").slice(1).join(":");
    const mc = selectMissionsCampaignById(useAppStore.getState(), key);
    if (!mc) return;
    const store = campaignKeyOf(mc.campaign);
    const id = window.setTimeout(() => {
      const at = open.indexOf(current);
      setFocus(store, open[(at + 1) % open.length]);
    }, ROTATE_MS);
    return () => window.clearTimeout(id);
  }, [inOrder, taskKey, ids, pips, key, setFocus]);
}

/** Behind the coin: the prize pill, the progress cell and the task pill. */
export function MissionContent({
  campaignKey,
  reduced,
  coin,
  announce,
}: LauncherContentProps) {
  const { t } = useTranslation();
  const live = useMissionView(campaignKey);
  useTaskRotation(live, campaignKey);
  // A task done: the view it leaves held at its end (full, its count at the
  // target) through its beat, then the next task's. Derived in the very
  // render the next task arrives in, so the bar never shows the next task
  // for a frame (Sebastian: "it feels like the whole bar is replaced when a
  // task is done, so it jumps a bit — I want it to be the same bar").
  // `paid`: the step's last task (the next view is another step, or none).
  const [held, setHeld] = useState<Beat | null>(null);
  const heldRef = useRef(held);
  heldRef.current = held;
  const settled = useRef(live);
  const liveRef = useRef(live);
  liveRef.current = live;
  // Tasks the live view moved on to during a beat, in order (one spin can
  // finish two — 👺 Belmont, T2): played one beat at a time, never a beat
  // restarted or dropped.
  const queue = useRef<MissionView[]>([]);
  const switching = (before: MissionView | null) =>
    !!live && !!before?.taskKey && before.taskKey !== live.taskKey;
  const beatOf = (v: MissionView, next: MissionView | null): Beat => {
    // Done, unless the next view still lists the task as open: then the
    // task on show moved (a swap), and the view is kept as it was.
    const id = v.taskKey?.split(":").slice(1).join(":");
    const i = next && id ? next.taskIds.split(",").indexOf(id) : -1;
    const swap =
      !!next && next.step === v.step && i >= 0 && next.pips[i] !== "d";
    return swap
      ? { view: v, paid: false, kind: "swap" }
      : {
          view: { ...v, ratio: 1, value: v.total ?? v.value },
          paid: !next || next.step !== v.step,
          kind: "done",
        };
  };
  useLayoutEffect(() => {
    if (heldRef.current) {
      const last = queue.current[queue.current.length - 1];
      if (live?.taskKey && live.taskKey !== last?.taskKey)
        queue.current.push(live);
      return;
    }
    const before = settled.current;
    if (!before || !switching(before)) {
      settled.current = live;
      return;
    }
    queue.current = live ? [live] : [];
    setHeld(beatOf(before, live));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.taskKey]);
  // The queued task's latest progress, as it comes.
  const queued = queue.current;
  if (held && live && queued[queued.length - 1]?.taskKey === live.taskKey)
    queued[queued.length - 1] = live;
  // Within one task, the live view as it comes.
  if (!held && live && settled.current?.taskKey === live.taskKey)
    settled.current = live;
  const doneBeat =
    held ??
    (settled.current && switching(settled.current)
      ? beatOf(settled.current, live)
      : null);
  const view = doneBeat?.view ?? live;
  const paid = doneBeat?.paid ?? false;
  const ready = view !== null;
  const heldKey = held?.view.taskKey ?? null;
  const heldKind = held?.kind ?? null;
  // Whether the last smack took the prize into the coin (a payout): the
  // next task's entry brings it back out; otherwise it stayed put.
  const prizeIn = useRef(false);
  // The beat's end: the next task thrown out — or, when the live view has
  // already moved past it too, that task's own beat chained on.
  useEffect(() => {
    if (!held) return;
    const id = window.setTimeout(
      () => {
        const [next, ...rest] = queue.current;
        if (next && rest.length > 0) {
          queue.current = rest;
          settled.current = next;
          setHeld(beatOf(next, rest[0]));
          return;
        }
        queue.current = [];
        settled.current = liveRef.current;
        setHeld(null);
      },
      reduced
        ? held.kind === "swap"
          ? 0
          : TASK_DONE_STILL_MS
        : beatTimes(held.paid, held.kind).throwAt
    );
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKey, reduced]);
  // The done bar's win, once its fill is full: brightened (gold on a
  // payout), the shine, the check, the gleam.
  const [won, setWon] = useState(false);
  // In order, the step the map takes after the slam: the next task's lock
  // broken and its dot lit (cleared with the beat).
  const [advanced, setAdvanced] = useState(false);
  useEffect(() => {
    setAdvanced(false);
    if (!won || heldKind !== "done" || !held?.view.inOrder) return;
    const id = window.setTimeout(() => setAdvanced(true), ADVANCE_AT_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [won, heldKey]);
  // How many pips the map's row fits between the coin and the bar's end
  // (measured: the pip's box and gap are the sheet's; kept on resize).
  const [mapWindow, setMapWindow] = useState(MAP_WINDOW_MAX);
  const windowStart = useRef({ ids: "", n: 0, start: 0 });
  useLayoutEffect(() => {
    const root = scope.current;
    const cell = root?.querySelector<HTMLElement>('[data-part="cell"]');
    const pips = root?.querySelector<HTMLElement>(".igw-mission__pips");
    const pip = pips?.querySelector<HTMLElement>(".igw-mission__pip");
    if (!cell || !pips || !pip) return;
    const measure = () => {
      // The room: the cell's width past the beads' start, less the end
      // pad. Layout geometry, not rects: the cell scales in on entry.
      const room = cell.clientWidth - pips.offsetLeft - PIPS_END_PX;
      const w = pip.offsetWidth;
      // The pitch's gap is the sheet's (`--igw-map-gap`: the track's gap,
      // or the path's margin between pips).
      const gap =
        parseFloat(getComputedStyle(pips).getPropertyValue("--igw-map-gap")) ||
        parseFloat(getComputedStyle(pip).marginLeft) ||
        0;
      const pitch = w + gap;
      const total = view?.pips.length ?? 0;
      const max = view?.inOrder ? MAP_WINDOW_MAX_PATH : MAP_WINDOW_MAX;
      const floor = Math.min(MAP_WINDOW_MIN, total);
      // The window is `n` pips and `n - 1` gaps wide — plus one pitch when
      // tasks lie beyond it, where the next pip peeks and fades out (the
      // "more" cue); never under the floor (Sebastian's 4-pip floor).
      const fits = Math.max(1, Math.floor((room + gap) / pitch));
      // All of them, or as many as fit less the pitch the next one peeks
      // in, never under the floor; "more" whenever any task is beyond the
      // window.
      const n =
        fits >= total
          ? Math.min(max, total)
          : Math.max(floor, Math.min(max, fits - 1));
      pips.style.setProperty("--igw-map-more", n < total ? "1" : "0");
      setMapWindow(n);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(cell);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, view?.pips, view?.inOrder, view?.prize]);
  useEffect(() => {
    setWon(false);
    if (!heldKey || heldKind === "swap") return;
    const id = window.setTimeout(
      () => setWon(true),
      reduced ? 0 : TASK_WON_AT_MS
    );
    return () => window.clearTimeout(id);
  }, [heldKey, heldKind, reduced]);
  const taskKey = view?.taskKey ?? null;
  const [scope, animateIn] = useAnimate<HTMLSpanElement>();
  // Progress on a task off show, mid-turn: its bead kicks — the bar stays
  // (MISSION-02; "a turn is a turn", Sebastian 2026-10-22).
  const counts = useStepCounts(campaignKey);
  const countsBefore = useRef(counts);
  useEffect(() => {
    const before = new Map(
      countsBefore.current
        .split("|")
        .map((p) => p.split("=") as [string, string])
    );
    countsBefore.current = counts;
    if (!live) return;
    const onShow = live.taskKey?.split(":").slice(1).join(":");
    const ids = live.taskIds.split(",");
    const runs: AnimationPlaybackControls[] = [];
    const tints: { pip: HTMLElement; id: number }[] = [];
    for (const part of counts.split("|")) {
      const [id, count] = part.split("=");
      if (!id || id === onShow || !before.has(id) || before.get(id) === count)
        continue;
      const pip = scope.current?.querySelector<HTMLElement>(
        `.igw-mission__pip[data-pip="${ids.indexOf(id)}"]`
      );
      if (!pip) continue;
      if (reduced) {
        // Still: its rim takes the token for a second instead (MOTION-02).
        pip.setAttribute("data-moved", "");
        tints.push({
          pip,
          id: window.setTimeout(
            () => pip.removeAttribute("data-moved"),
            MOVED_TINT_MS
          ),
        });
        continue;
      }
      let run = animate(pip, { scale: 1.15 }, PIP_KICK_UP);
      runs.push(run);
      void run.then(() => {
        run = animate(pip, { scale: 1 }, PIP_KICK);
        runs.push(run);
        void run.then(() => pip.style.removeProperty("transform"));
      });
    }
    return () => {
      runs.forEach((r) => r.stop());
      tints.forEach(({ pip, id }) => {
        window.clearTimeout(id);
        pip.removeAttribute("data-moved");
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts]);
  const cellRef = useRef<HTMLSpanElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  // The bar's turn to gold (`--igw-won` on the cell, 0 → 1), and the count
  // fading as the check lands; both back at once with the next task.
  const wonShare = useMotionValue(0);
  const goldShare = useMotionValue(0);
  const flow = useMotionValue(0);
  useLayoutEffect(() => {
    const writer = (name: string) => (v: number) =>
      cellRef.current?.style.setProperty(name, String(v));
    const offs = (
      [
        [wonShare, "--igw-won"],
        [goldShare, "--igw-gold"],
        [flow, "--igw-flow"],
      ] as const
    ).map(([value, name]) => {
      const write = writer(name);
      write(value.get());
      return value.on("change", write);
    });
    return () => offs.forEach((off) => off());
  }, [wonShare, goldShare, flow, ready]);
  useEffect(() => {
    if (!won) {
      wonShare.set(0);
      goldShare.set(0);
      flow.set(0);
      return;
    }
    const el = valueRef.current;
    const at = reduced ? { duration: 0 } : WON_SPRING;
    const turn = animate(wonShare, 1, at);
    const gold = paid ? animate(goldShare, 1, at) : undefined;
    // Still under reduced motion: the crest stays at the front.
    const across = reduced ? undefined : animate(flow, 1, FLOW_SPRING);
    const fade = el
      ? animate(el, { opacity: 0 }, reduced ? { duration: 0 } : WON_SPRING)
      : undefined;
    return () => {
      turn.stop();
      gold?.stop();
      across?.stop();
      fade?.stop();
      el?.style.removeProperty("opacity");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [won, wonShare, goldShare, flow, reduced]);
  // Said as the check lands (👺 Belmont, T5): the task done — or, on a
  // payout, the step and its prize.
  useEffect(() => {
    if (!won || !held) return;
    const v = held.view;
    announce(
      held.paid
        ? [
            t("missions.stepDone", { current: v.step }),
            v.prize
              ? `${t("igwButton.prize")} ${formatPrizeTitle(v.prize, t)}`
              : null,
          ]
        : [v.task, t("missions.taskDone")]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [won]);
  // The smack: the done bar and the task — the prize too, on a payout —
  // wound up, then struck back under the coin in turn along x (metal, no
  // scale), the line going out on its own; the coin takes the hit
  // (the shell's, `coin`), on whose recoil the next task is thrown out (the
  // entry below). They stay collapsed until then.
  useEffect(() => {
    const root = scope.current;
    if (!held || reduced || !root) return;
    const times = beatTimes(held.paid, held.kind);
    const part = (name: string) =>
      root.querySelector<HTMLElement>(`[data-part="${name}"]`);
    // Back under the coin along x, metal (`igw-button/metal`; D-128: every
    // piece leaves by its entry reversed): no scale, so the slanted chips
    // keep their shape on the way in; a small run forward first, then the
    // strike on the metal spring. At full ink — the coin and the set's clip
    // hide them until the next step's are thrown out from the same place.
    const drawnIn = { x: METAL_FROM_X };
    const drawnBack = { x: 4 };
    // A task done within the step: only the line goes (the beads update in
    // place, the cell stays — Sebastian, 2026-10-20: "they go in and out
    // of the coin instead of just updating the task map"). A payout: the
    // cell and the prize chip go into the coin, the next step's come out.
    const parts: [HTMLElement | null, object, object][] = [];
    if (held.paid) {
      parts.push([part("cell"), drawnBack, drawnIn]);
      // Its checklist with it (👺 Belmont, B1), and the prize chip.
      parts.push([part("beads"), drawnBack, drawnIn]);
      parts.push([part("map"), drawnBack, drawnIn]);
    } else {
      // The done fill fades as the line leaves — never a bar emptying in
      // place (👺 Belmont, B6); the next task's fill grows in (the entry).
      parts.push([part("fill"), {}, { opacity: 0 }]);
    }
    parts.push([part("task-line"), { x: 3 }, LINE_OUT]);
    prizeIn.current = held.paid;
    const runs: AnimationPlaybackControls[] = [];
    const timers: number[] = [];
    let hit = false;
    const at = (ms: number, run: () => void) =>
      timers.push(window.setTimeout(run, ms));
    parts.forEach(([el, windup, strike], i) => {
      if (!el) return;
      const from = times.smack + i * SMACK_STAGGER_MS;
      at(from, () => runs.push(animate(el, windup, SMACK_WINDUP)));
      at(from + SMACK_WINDUP_MS, () =>
        runs.push(
          animate(el, strike, strike === drawnIn ? METAL_SPRING : SMACK_STRIKE)
        )
      );
    });
    // The coin takes the hit on a payout only.
    if (held.paid) {
      at(times.hit, () => {
        hit = true;
        coin.hit();
      });
      // The recoil outlives the beat (the throw ends it at the same moment).
      at(times.recoil, () => {
        hit = false;
        coin.release();
      });
    }
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      runs.forEach((r) => r.stop());
      // A beat cut short between the hit and the recoil lets the coin go.
      if (hit) coin.release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKey, reduced]);
  // Progress on the task on show kicks its pip at the hit (Sebastian,
  // 2026-10-19: "we need some orchestration for progress made"): the same
  // task, a new count — never the first count or a swap.
  const kicked = useRef<{ task: string | null; value: string | null }>({
    task: null,
    value: null,
  });
  // The count and the fill's share on show: a new count lands as a beat
  // (`VALUE_WINDUP` … `VALUE_HIT`) — they change at the hit. Anything else
  // (the first count, a new task, reduced motion) lands at once.
  const nextValue = view?.value ?? null;
  const nextRatio = view?.ratio ?? 0;
  const [hit, setHit] = useState({
    task: taskKey,
    value: nextValue,
    ratio: nextRatio,
  });
  const hitRef = useRef(hit);
  hitRef.current = hit;
  useEffect(() => {
    const was = kicked.current;
    kicked.current = { task: hit.task, value: hit.value };
    if (
      reduced ||
      !was.task ||
      was.task !== hit.task ||
      was.value === null ||
      hit.value === null ||
      was.value === hit.value
    )
      return;
    // The bead itself (no path link hangs off it any more; transforms
    // don't move its neighbours in the track).
    const box = scope.current?.querySelector<HTMLElement>(
      ".igw-mission__pip--current"
    );
    if (!box) return;
    // The event also restarts the bead's own beat (the ripple).
    box.getAnimations({ subtree: true }).forEach((a) => {
      a.cancel();
      a.play();
    });
    // Two springs, not a keyframe list: a spring takes two keyframes only.
    // A nudge on the whole bead (MISSION-02: ~115%; 145% was for a glyph
    // smaller than its box — 👺 Belmont, B7).
    let run = animate(box, { scale: 1.2 }, PIP_KICK_UP);
    void run.then(() => {
      run = animate(box, { scale: 1 }, PIP_KICK);
      void run.then(() => box.style.removeProperty("transform"));
    });
    return () => run.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit.value, hit.task, reduced]);
  // Which beat is the latest: a pop finishing clears the value's inline
  // transform only if no newer beat has taken it over.
  const beat = useRef(0);
  useLayoutEffect(() => {
    const before = hitRef.current;
    const next = { task: taskKey, value: nextValue, ratio: nextRatio };
    if (
      before.task === next.task &&
      before.value === next.value &&
      before.ratio === next.ratio
    )
      return;
    const el = valueRef.current;
    const counting =
      !reduced &&
      !!el &&
      before.task === next.task &&
      before.value !== null &&
      Number(before.value.replace(/\D/g, "")) !== 0 &&
      before.value !== next.value;
    const mine = ++beat.current;
    if (!el || !counting) {
      // At once: a wind-up cut short snaps home, no spring (the hit's
      // carries velocity — it would kick a value already at rest, 👺 N2).
      if (el) {
        void animate(el, { x: 0, scale: 1 }, { duration: 0 });
        el.style.removeProperty("transform");
      }
      setHit(next);
      return;
    }
    const windup = animate(el, { x: -VALUE_WINDUP_PX }, VALUE_WINDUP);
    let release: number | undefined;
    const popOut = () => {
      release = undefined;
      void animate(el, { scale: 1 }, VALUE_POP_OUT).then(() => {
        if (beat.current === mine) el.style.removeProperty("transform");
      });
    };
    let swell: AnimationPlaybackControls | undefined;
    const id = window.setTimeout(() => {
      windup.stop();
      setHit(next);
      // The hit: right past its place and back, and the pop — a quick
      // swell, released at once (a spring's "done" lags). The hit runs on
      // if another count arrives (its wind-up takes the x over); the pop is
      // released then.
      void animate(el, { x: 0 }, VALUE_HIT);
      swell = animate(el, { scale: 1.18 }, VALUE_POP_IN);
      release = window.setTimeout(() => {
        swell?.stop();
        popOut();
      }, VALUE_POP_HOLD_MS);
    }, VALUE_HIT_AT_MS);
    return () => {
      window.clearTimeout(id);
      windup.stop();
      if (release !== undefined) {
        window.clearTimeout(release);
        swell?.stop();
        popOut();
      }
    };
  }, [taskKey, nextValue, nextRatio, reduced]);
  const ratio = hit.ratio;
  // Before any progress: no "0", only the etched glyph; the first count
  // brings the value in (Sebastian, 2026-10-15).
  const started = ratio > 0;
  // The fill's share, sprung to each new value (set at once on mount, on a
  // new task and under reduced motion), written to the cell's
  // `--igw-progress` by hand: the cell is a plain element so its entry can
  // own its transform.
  const progress = useMotionValue(ratio);
  const target = useRef(ratio);
  target.current = ratio;
  const slideEnd = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(slideEnd.current), []);
  // Each frame of the fill: its share to the cell, and the value riding its
  // front — just inside the leading edge, etched into it in a dark ink of
  // the bright front; just ahead of it on the track, in the light ink, while
  // the fill is too short to hold it (Sebastian, 2026-10-15: "the progress
  // value will follow the front part of the progress bar"; a puck behind it
  // was "a bit busy", the etched front tried instead — values can be long,
  // money tasks count in amounts). Where neither side has room (a long
  // amount on a short bar), it stops at the bar's end and its ink splits at
  // the fill's edge: dark on the fill, light on the track (IGB-MAT-05,
  // 👺 Belmont, B2).
  useLayoutEffect(() => {
    const write = (raw: number) => {
      const cell = cellRef.current;
      if (!cell) return;
      // The spring's overshoot stays inside the bar: the fill and the value
      // never pass the cell's end (Sebastian, 2026-10-20: "make sure it
      // doesn't go outside of the shape, it breaks the illusion").
      const v = Math.min(1, Math.max(0, raw));
      cell.style.setProperty("--igw-progress", String(v));
      const value = valueRef.current;
      if (!value) return;
      // Where the cell shows past the coin (`--igw-mission-start`).
      const css = getComputedStyle(cell);
      const start =
        (parseFloat(css.getPropertyValue("--igw-rank-left")) || 0) +
        (parseFloat(css.getPropertyValue("--igw-rank-size")) || 0) * 0.42;
      const end = cell.clientWidth;
      const span = end - start;
      const width = value.offsetWidth;
      const pad = 7;
      const fits = (share: number, margin: number) =>
        start + share * span - pad - width >= start + 4 + margin;
      // Inside or ahead: a band around the threshold (no flicker as the
      // front hovers there), and a side is only ever taken toward where the
      // spring is heading — its overshoot never flips the value back
      // (👺 Belmont, B7).
      const was = value.hasAttribute("data-inside");
      const now = fits(v, was ? -VALUE_SIDE_BAND : VALUE_SIDE_BAND);
      const inside = now === fits(target.current, 0) ? now : was;
      const front = start + v * span;
      const x = inside
        ? front - pad - width
        : Math.min(front + pad, end - pad - width);
      const split = !inside && front > x ? Math.min(front - x, width) : 0;
      value.style.setProperty("--igw-value-x", `${x}px`);
      value.style.setProperty("--igw-value-split", `${split}px`);
      value.toggleAttribute("data-split", split > 0);
      // The fill's bright front spans the whole value (its stops measured
      // from the leading edge, not as shares of the fill).
      cell.style.setProperty("--igw-value-w", inside ? `${width}px` : "0px");
      if (inside !== was) {
        value.toggleAttribute("data-inside", inside);
        // A side change lands, it doesn't teleport — and the value never
        // leaves the screen (Sebastian, 2026-10-22: "it should not disappear
        // at all"; it used to fade in from 0 at its new place): it slides
        // there (`data-sliding`, a short ease on its translate) — not on a
        // done task's bar, where the count has given way to the check (the
        // fill jumping full moves it inside).
        if (!reduced && !cell.hasAttribute("data-won")) {
          value.setAttribute("data-sliding", "");
          window.clearTimeout(slideEnd.current);
          slideEnd.current = window.setTimeout(
            () => value.removeAttribute("data-sliding"),
            VALUE_SIDE_SLIDE_MS
          );
        }
      }
    };
    write(progress.get());
    const off = progress.on("change", write);
    // Not only per frame of the fill: a resize (a size switch, a late
    // layout) or a new count's width moves the value and its split too
    // (👺 Belmont, B2 — the split landed stale at the phone size).
    const ro = new ResizeObserver(() => write(progress.get()));
    if (cellRef.current) ro.observe(cellRef.current);
    if (valueRef.current) ro.observe(valueRef.current);
    return () => {
      off();
      ro.disconnect();
    };
  }, [progress, ready, reduced, started]);
  // The fill: a new count springs to it; a new task starts its bar where it
  // is (its own grow, below), never running back from the done one.
  const fillTask = useRef(hit.task);
  useEffect(() => {
    const switched = fillTask.current !== hit.task;
    fillTask.current = hit.task;
    if (reduced || switched) {
      progress.set(ratio);
      return;
    }
    const run = animate(progress, ratio, PROGRESS_SPRING);
    return () => run.stop();
  }, [progress, ratio, reduced, hit.task]);
  // A new task, thrown out of the coin after the done one's smack: the
  // content's own entry (below), its bar growing in length only (Sebastian,
  // 2026-10-16: "the new task has the same transition as the entry one for
  // missions, so we can reuse that"; "I only want to scale the X and not the
  // Y axis on the bar here").
  const grownTask = useRef(taskKey);
  useEffect(() => {
    const before = grownTask.current;
    grownTask.current = taskKey;
    if (reduced || !before || !taskKey || before === taskKey) return;
    const prize = prizeIn.current;
    prizeIn.current = false;
    // The cell regrows only when it went into the coin (a payout); a task
    // done within the step keeps it and swaps the line.
    return enter(0, prize, prize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskKey, reduced]);
  // The task line on its banner: never truncated (a task line is priority
  // content). Measured in layout px: its type shrinks to its 9px floor
  // first; still wider than the banner's room, it glides over the hidden
  // part and back (the prize's marquee, `prize-marquee-glide`), never an
  // ellipsis (Sebastian, 2026-10-19: "we have truncated the task label,
  // which is forbidden"). Kept on resize.
  useLayoutEffect(() => {
    const line = scope.current?.querySelector<HTMLElement>(
      ".igw-mission__task-line"
    );
    const row = line?.parentElement;
    if (!line || !row) return;
    const fit = () => {
      line.style.removeProperty("font-size");
      line.classList.remove("prize-marquee");
      line.style.removeProperty("--marquee-distance");
      line.style.removeProperty("--marquee-duration");
      const css = getComputedStyle(row);
      const room =
        row.clientWidth -
        parseFloat(css.paddingLeft) -
        parseFloat(css.paddingRight);
      const natural = line.scrollWidth;
      const px = parseFloat(getComputedStyle(line).fontSize) || 11;
      // The line's fitted size, for the count to follow it down (capped at
      // 10 in the sheet; Sebastian, 2026-10-21: "count follows the line,
      // min(10, task px)" — the count is never bigger than its line).
      const fitted =
        natural <= room ? px : Math.max(TASK_FLOOR_PX, (px * room) / natural);
      cellRef.current?.style.setProperty(
        "--igw-mission-line-px",
        `${fitted}px`
      );
      if (natural <= room) return;
      line.style.fontSize = `${fitted}px`;
      const overflow = line.scrollWidth - room;
      if (overflow <= 1 || reduced) return;
      // A reading pace (~30px/s) with a rest at each end.
      line.style.setProperty("--marquee-distance", `-${overflow}px`);
      line.style.setProperty("--marquee-duration", `${overflow / 30 + 1.4}s`);
      line.classList.add("prize-marquee");
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(row);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, view?.task, reduced]);
  // The first count brings the value in (above, `started`).
  const doneShowing = doneBeat !== null;
  const shownValue = useRef(started);
  useLayoutEffect(() => {
    const was = shownValue.current;
    shownValue.current = started;
    const el = valueRef.current;
    // Not on a done task's bar: its count is there to give way to the
    // check, and an entrance fading it in would win over that fade.
    if (!started || was || !el || reduced || doneShowing) return;
    // Held hidden (before paint) until the fill has started to grow, then in (Sebastian: "I want the value to transition
    // in, delay it a bit").
    el.style.opacity = "0";
    el.style.transform = "scale(0.5)";
    const run = animate(
      el,
      { scale: [0.5, 1], opacity: [0, 1] },
      { ...VALUE_IN, delay: VALUE_IN_DELAY }
    );
    void run.then(() => {
      el.style.removeProperty("transform");
      el.style.removeProperty("opacity");
    });
    return () => run.stop();
  }, [started, reduced, doneShowing]);
  // The entry: the start poses set (before paint on mount — no flash of the
  // settled layout), then the springs from explicit start values; the
  // inline values cleared at rest. `at` is when the cell starts (s),
  // `withPrize` whether the prize comes in too.
  function enter(at: number, withPrize = true, withCell = true) {
    const part = (name: string) =>
      scope.current?.querySelector<HTMLElement>(`[data-part="${name}"]`) ??
      null;
    const runs: AnimationPlaybackControls[] = [];
    const timers: number[] = [];
    let running = true;
    const settle = (el: HTMLElement) => {
      if (!running) return;
      el.style.removeProperty("transform");
      el.style.removeProperty("opacity");
      el.style.removeProperty("z-index");
    };
    // Each part held at its start pose, then sprung from explicit start
    // values: a delayed spring shows the element's own style until it
    // starts. The pose is set twice: inline (before paint, on mount) and as
    // motion's own values, which a smack before it left elsewhere (its
    // springs read those, not the inline style).
    const hold = (
      el: HTMLElement,
      transform: string,
      values: Record<string, number | string>
    ) => {
      el.style.transform = transform;
      if (values.opacity !== undefined)
        el.style.opacity = String(values.opacity);
      void animate(el, values, { duration: 0 });
    };
    // A piece slid out from under the coin: held at the start pose, then
    // the metal spring (x) and a quick fade, cleared at rest.
    const slideIn = (el: HTMLElement, delay: number) => {
      hold(el, `translateX(${METAL_FROM_X})`, {
        x: METAL_FROM_X,
        opacity: 0,
      });
      const slide = animateIn(
        el,
        { x: [METAL_FROM_X, "0%"] },
        { ...METAL_SPRING, delay }
      );
      const fade = animateIn(
        el,
        { opacity: [0, 1] },
        { duration: 0.07, delay }
      );
      runs.push(slide, fade);
      void Promise.all([slide, fade]).then(() => settle(el));
    };
    const cell = withCell ? part("cell") : null;
    if (cell) {
      // Out from under the coin along x only, metal (`igw-button/metal`;
      // Sebastian, 2026-10-21: "use this type of entry on the other
      // campaign types as well, stagger its different elements a bit"): no
      // scale, no swell — the cell, then its checklist, then the prize.
      slideIn(cell, at);
      const beads = part("beads");
      if (beads) slideIn(beads, at + METAL_STAGGER);
    } else {
      // A new task on the same cell: its fill grows from the coin end as
      // the line arrives (the done one faded with the smack).
      const fill = part("fill");
      if (fill) {
        hold(fill, "scaleX(0)", { scaleX: 0, opacity: 1 });
        const grow = animateIn(
          fill,
          { scaleX: [0, 1] },
          { ...ENTRY_GROW, delay: at + ENTRY_TASK_AT - ENTRY_AT }
        );
        runs.push(grow);
        void grow.then(() => settle(fill));
      }
    }
    // The prize is the chip under the cell: it comes in with the map, no
    // banner of its own any more.
    // The map pops in with the task only when it is a new map — the first
    // entry and a new step (with the prize); within a step it stays put
    // while the plate goes into the coin and the next one is thrown out
    // (Sebastian, 2026-10-19: "the map can stay").
    const map = withPrize ? part("map") : null;
    // The prize chip: out from under the coin like the rest, a beat after
    // the checklist on an entry (its own beat on a new step, with the task).
    if (map)
      slideIn(
        map,
        withCell ? at + 2 * METAL_STAGGER : at + ENTRY_TASK_AT - ENTRY_AT
      );
    const line = part("task-line");
    if (line) {
      // The title is a row of the cell now (`CaptionCell`), so it grows out
      // with it; only the line itself slides in — on the first entry and on
      // a new task alike (the old line went off with the smack).
      hold(line, "translateX(10px)", { ...LINE_IN_FROM });
      const slide = animateIn(
        line,
        { opacity: [0, 1], x: [10, 0] },
        { ...ENTRY_SETTLE, delay: at + ENTRY_TASK_AT - ENTRY_AT }
      );
      runs.push(slide);
      void slide.then(() => settle(line));
    }
    return () => {
      running = false;
      // An entry cut short (a switch away) leaves nothing behind.
      timers.forEach((id) => window.clearTimeout(id));
      runs.forEach((r) => r.stop());
    };
  }
  // The entry, once, when the content first has something to show (the
  // set itself grows out of the coin first: `ENTRY_AT`).
  const entered = useRef(false);
  useLayoutEffect(() => {
    if (!ready || entered.current) return;
    entered.current = true;
    if (reduced) return;
    return enter(ENTRY_AT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
  // The cell's width: once per step, the larger of two needs, clamped to
  // the cell's bounds — the longest of the step's task lines (with the
  // band's pads), and the step's prize at its floor plus the chip's offset
  // from the cell (its slant and its 16px step), so the prize never glides
  // while the bounds have room (the prize wins the space; 👺 Belmont, B2) —
  // so a task done within the step never moves the cell's edge (B3); the
  // chip under it is cut from the same width (B4). A line past the
  // narrowest cell at the band's type shrinks to the line's 9px floor
  // FIRST, and only a line still past it at the floor widens the cell, up
  // to its bound (Sebastian, 2026-10-21: "if it's too long we scale it
  // down, then we increase the width, with sensible min and max widths");
  // past that it glides (below). Written to the content's root for both to
  // read.
  useLayoutEffect(() => {
    const root = scope.current;
    const cell = root?.querySelector<HTMLElement>('[data-part="cell"]');
    const band = cell?.querySelector<HTMLElement>(".igw-mission__task");
    const lines = view?.taskLines;
    if (!root || !cell || !band || !lines) return;
    const fit = () => {
      const canvas = document.createElement("canvas").getContext("2d");
      if (!canvas) return;
      const bcs = getComputedStyle(band);
      const px = parseFloat(bcs.fontSize) || 11;
      // The band's letter-spacing, per character (measureText ignores it),
      // an em share so the floor's is scaled with its type.
      const tracking = parseFloat(bcs.letterSpacing) || 0;
      const longestAt = (size: number) => {
        canvas.font = `${bcs.fontWeight} ${size}px ${bcs.fontFamily}`;
        const track = (tracking * size) / px;
        return Math.max(
          ...lines
            .split("\n")
            .map((l) => canvas.measureText(l).width + l.length * track)
        );
      };
      const pads = parseFloat(bcs.paddingLeft) + parseFloat(bcs.paddingRight);
      const ccs = getComputedStyle(cell);
      const min = parseFloat(ccs.minWidth) || 0;
      const max = parseFloat(ccs.maxWidth) || Infinity;
      // A few px of slack: the canvas measure runs a little short of the
      // laid-out line (kerning, sub-pixel tracking), and a line a pixel past
      // its room would glide instead of standing.
      const slack = 4;
      const atType = longestAt(px) + pads + slack;
      const taskNeed =
        atType <= min ? atType : longestAt(TASK_FLOOR_PX) + pads + slack;
      // The prize's need: its line at the prize's floor, with the line's
      // pads, plus how much narrower the chip is than the cell (its slant
      // offset and step, read from the rendered pair).
      let prizeNeed = 0;
      const chip = root.querySelector<HTMLElement>('[data-part="prize"]');
      const prizeLine = chip?.querySelector<HTMLElement>(
        ".igw-mission__prize-line"
      );
      if (chip && prizeLine && prizeLine.textContent) {
        const pcs = getComputedStyle(prizeLine);
        canvas.font = `${pcs.fontWeight} ${PRIZE_MIN_PX}px ${pcs.fontFamily}`;
        const text = canvas.measureText(prizeLine.textContent).width;
        const ppads =
          parseFloat(pcs.paddingLeft) + parseFloat(pcs.paddingRight);
        const offset = cell.offsetWidth - chip.offsetWidth;
        prizeNeed = text + ppads + slack + offset;
      }
      const need = Math.max(taskNeed, prizeNeed);
      const width = Math.min(max, Math.max(min, Math.ceil(need)));
      root.style.setProperty("--igw-mission-cell-w", `${width}px`);
    };
    fit();
    // A cold load measures the fallback font: fit again once the band's
    // face has landed (👺 Belmont, N1).
    let live = true;
    void document.fonts?.ready.then(() => live && fit());
    // The band's pads and type scale with the button.
    const ro = new ResizeObserver(fit);
    ro.observe(band);
    return () => {
      live = false;
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, view?.taskLines, view?.prize]);
  // The map's window: up to `mapWindow` pips with the task on show second.
  // Any order holds its place while the task on show is inside it short of
  // the dimmed edge, so a quiet swap doesn't slide the map; in order it
  // follows the task on show, so every done task moves the map one pitch
  // (👺 Belmont, B6/N1). Committed after render, not during it.
  const geom = (() => {
    if (!view) return null;
    const all = [...view.pips];
    const at = Math.max(0, all.indexOf("c"));
    const n = Math.max(1, Math.min(mapWindow, all.length));
    const held = windowStart.current;
    const hold =
      !view.inOrder &&
      held.ids === view.taskIds &&
      held.n === n &&
      at > held.start &&
      at < held.start + n - 1;
    const start = hold
      ? held.start
      : Math.max(0, Math.min(at - 1, all.length - n));
    return { all, at, n, start };
  })();
  useLayoutEffect(() => {
    if (!view || !geom) return;
    windowStart.current = { ids: view.taskIds, n: geom.n, start: geom.start };
  });
  if (!view || !geom) return null;
  return (
    <span
      ref={scope}
      className="igw-mission"
      // No idle motion under reduced motion (the dev frame's override too).
      data-still={reduced || undefined}
    >
      {/* The task's title over its bar, ONE two-row cell (Sebastian,
          2026-10-20: "task progress and task bar paired, still two rows, a
          shared component"); the title leads at 11px, the count under it
          at 10px is the smaller addition (Alex, 2026-10-20). */}
      <CaptionCell
        ref={cellRef}
        className="igw-row igw-row--hero igw-mission__cell"
        data-part="cell"
        // No idle sheen under reduced motion (the dev frame's override too).
        data-still={reduced || undefined}
        data-won={won || undefined}
        data-gold={(won && paid) || undefined}
        captionProps={{ className: "igw-mission__task", "data-part": "task" }}
        caption={
          // `upright`: the line turns back to read on the button's mirrored
          // side (a shrink-wrapped item, so it stays by the coin).
          <span
            className="igw-mission__task-line upright"
            data-part="task-line"
          >
            {view.task}
          </span>
        }
      >
        {/* The task kind's own glyph (Sebastian: "the task's own glyph"),
            etched into the track; it stays, the fill passes over it ("the
            glyph can stay and the progress just goes over it"). */}
        <span className="igw-mission__glyph" aria-hidden="true">
          <TaskKindIcon kind={view.kind} />
        </span>
        <span className="igw-mission__track" aria-hidden="true">
          <span className="igw-mission__fill" data-part="fill" />
        </span>
        {started && hit.value !== null && (
          <span ref={valueRef} className="igw-mission__value">
            {/* The leaderboard's points treatment; the number swaps at the
                hit (no roll). */}
            <Badge variant="text" color="foreground" size="inherit">
              {hit.value}
            </Badge>
            {/* The split's dark ink: the same digits over the first, clipped
                to the part on the fill. */}
            <span className="igw-mission__value-dark" aria-hidden="true">
              <Badge variant="text" color="foreground" size="inherit">
                {hit.value}
              </Badge>
            </span>
          </span>
        )}
        {won && (
          <motion.span
            className="igw-mission__check"
            aria-hidden="true"
            // Still under reduced motion: there at once.
            initial={reduced ? false : CHECK_FROM}
            animate={{
              scale: 1,
              rotate: 0,
              opacity: 1,
              transition: CHECK_STAMP,
            }}
          >
            <StepDoneIcon />
          </motion.span>
        )}
        {won && <Gleam variant="won-1" />}
        {won && paid && <Gleam variant="won-2" />}
      </CaptionCell>
      {view.pips.length > 1 &&
        /* The task map, strung on the cell's TOP rim — the title band's
             edge (Sebastian, 2026-10-20: "the task map should be on top of
             the task label row, in this manner, a bit top — the most
             sensible place"): one bead per task (D-069/D-074, for both
             kinds of step): done its ✓ alone, locked its lock alone (which
             breaks in half when the task unlocks), the paying task its star
             alone, the task on show a ring with its breathing dot, to do a
             hollow ring; in order, hairline links between them. */
        (() => {
          // A window of up to `mapWindow` pips with the task on show
          // second, on a track that slides as the window moves (the
          // sheet); the edge pips with more beyond them are dimmed. On a
          // task done the map stays, and at the check's impact the
          // task's pip slams into its ✓ (Sebastian, 2026-10-19).
          const { all, at, n, start } = geom;
          const slam = won && heldKind === "done";
          return (
            <span
              className={cn(
                "igw-mission__pips",
                view.inOrder && "igw-mission__pips--path"
              )}
              data-part="beads"
              style={
                {
                  "--igw-map-n": n,
                  "--igw-map-start": start,
                } as CSSProperties
              }
            >
              <span className="igw-mission__pips-window">
                <span className="igw-mission__pips-track">
                  {all.map((raw, idx) => {
                    // The edge pips with more beyond them: dimmed.
                    // (never the task on show itself)
                    // The left edge, with beads slid off before it: dimmed.
                    // (The right edge's cue is the next bead peeking and
                    // fading in the window's extra pitch — 👺 Belmont B5.)
                    // (a done one only — a dimmed open ring would read as
                    // locked, 👺 Belmont B5)
                    const dim =
                      idx !== at && idx === start && start > 0 && raw === "d";
                    const slams = slam && raw === "c";
                    // In order, after the slam: the next task's lock breaks
                    // and its dot lights — the path moves forward.
                    const lights = advanced && idx === at + 1 && raw === "l";
                    const state = slams ? "d" : lights ? "c" : raw;
                    // In order, the last task is the one that pays: its bead
                    // is in the prize's gold instead of the token, until it
                    // is done (Sebastian, 2026-10-19: "the last task should
                    // use a star, since completing that gives the prize" —
                    // the gold is the star now, D-134).
                    const prize =
                      view.inOrder && idx === all.length - 1 && state !== "d";
                    // The bead's box never moves or remounts: only its fill
                    // and glyph change — the ✓ stamps in, the locked shell
                    // breaks away (Sebastian, 2026-10-19: "the path doesn't
                    // disappear or morph at all, only the glyph").
                    return (
                      <span
                        key={idx}
                        data-pip={idx}
                        className={cn(
                          "igw-mission__pip",
                          state === "d" && "igw-mission__pip--done",
                          state === "c" && "igw-mission__pip--current",
                          state === "o" && "igw-mission__pip--open",
                          state === "l" && "igw-mission__pip--locked",
                          lights && "igw-mission__pip--lit",
                          dim && "igw-mission__pip--dim",
                          prize && "igw-mission__pip--prize"
                        )}
                      >
                        {lights && (
                          // The locked shell breaking away: two halves
                          // parting and fading (the sheet), over the on-show
                          // diamond coming up.
                          <span className="igw-mission__pip-break">
                            <span className="igw-mission__pip-half igw-mission__pip-half--l">
                              <span className="igw-mission__pip-shell" />
                            </span>
                            <span className="igw-mission__pip-half igw-mission__pip-half--r">
                              <span className="igw-mission__pip-shell" />
                            </span>
                          </span>
                        )}
                        <motion.span
                          key={state}
                          className="igw-mission__pip-glyph"
                          initial={slams && !reduced ? PIP_FROM : false}
                          animate={{
                            scale: 1,
                            rotate: 0,
                            opacity: 1,
                            transition: PIP_STAMP,
                          }}
                        >
                          {/* Only done carries a glyph: its ✓ alone. The
                              other states are the diamond's fill and rim
                              (the sheet). */}
                          {state === "d" && <StepDoneIcon />}
                        </motion.span>
                      </span>
                    );
                  })}
                </span>
              </span>
            </span>
          );
        })()}
      {view.task && view.prize && (
        // The step's prize, under the cell (the beads above are the map;
        // the "map" part is what goes into the coin with the cell on a
        // payout, and comes out with the next step).
        <span
          className="igw-mission__map igw-prize-chip-holder"
          data-part="map"
          aria-hidden="true"
        >
          {/* The prize chip: the button's one prize material (the prize
              drop's strip's — Sebastian, 2026-10-21: "this is how I want
              the prize to be styled in missions as well"; "I want the
              mission to have the chip shape"), a step narrower than the
              cell above it, its end on the chip's slant. The step's tasks
              are the beads on the cell above. */}
          <span className="igw-mission__prize igw-prize-chip" data-part="prize">
            {
              <span className="igw-mission__prize-line igw-prize-chip__prize">
                <PrizeText
                  size="inherit"
                  // The chip's deep ink on its gold, lifted by the chip's
                  // edge and halo (not the hard emboss).
                  tone="onGold"
                  // One step down, to the floor, then glides.
                  minScale={PRIZE_MIN_PX / PRIZE_PX}
                  shadow={false}
                  currencyMark={!view.catalog}
                  fit="marquee"
                  className="min-w-0"
                >
                  {formatButtonPrizeTitle(view.prize, t)}
                </PrizeText>
              </span>
            }
          </span>
        </span>
      )}
    </span>
  );
}
